// src/components/auth/LoginPage.tsx

import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/components/auth/AuthContext";
import { RequestAccessModal } from "./RequestAccessModal";
import logo from "@/assets/hermes_terminal_cubism.png";

const LoginPage: React.FC = () => {
  const { loginWithRedirect, isAuthenticated, isLoading: authLoading } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [rememberMe, setRememberMe] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const currentYear = new Date().getFullYear();
  const navigate = useNavigate();
  const location = useLocation();

  // Redirect authenticated users to the app
  useEffect(() => {
    if (isAuthenticated && !authLoading) {
      // Get the redirect path from location state, or default to /app/home
      const from = (location.state as { from?: { pathname: string } })?.from?.pathname || "/app/home";
      console.log("[LoginPage] User is authenticated, redirecting to:", from);
      navigate(from, { replace: true });
    }
  }, [isAuthenticated, authLoading, navigate, location]);

  const handleLogin = async () => {
    setIsLoading(true);
    try {
      // Auth0's loginWithRedirect doesn't accept parameters in the latest version
      // The remember me functionality is handled by the cacheLocation setting in auth0Config
      await loginWithRedirect();
    } catch (error) {
      console.error("Login error:", error);
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-purple-900 to-gray-900 flex items-center justify-center px-4 sm:px-6 lg:px-8">
      {/* Animated background effect */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-purple-800/20 via-purple-600/10 to-pink-800/20 animate-pulse" />
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-purple-500/10 rounded-full filter blur-3xl animate-blob" />
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-pink-500/10 rounded-full filter blur-3xl animate-blob animation-delay-2000" />
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-purple-600/10 rounded-full filter blur-3xl animate-blob animation-delay-4000" />
      </div>

      {/* Login Card */}
      <div className="relative z-10 w-full max-w-md">
        <div className="bg-gray-800/80 backdrop-blur-xl rounded-2xl shadow-2xl border border-purple-500/20 p-8 sm:p-10">
          {/* Logo and Title */}
          <div className="text-center mb-8">
            <div className="flex justify-center mb-4">
              <div className="relative">
                <div className="absolute inset-0 bg-purple-500/20 rounded-full filter blur-xl animate-pulse" />
                <img
                  src={logo}
                  alt="Hermes Terminal Logo"
                  className="h-20 w-20 sm:h-24 sm:w-24 relative z-10 drop-shadow-2xl"
                />
              </div>
            </div>
            <h1 className="text-3xl sm:text-4xl font-bold text-white mb-2 tracking-tight">
              Welcome to Hermes Terminal
            </h1>
            <p className="text-purple-300 text-sm sm:text-base">
              AI-Powered Crypto Analytics Platform. Elevate your trading.
            </p>
          </div>

          {/* Login Form */}
          <div className="space-y-6">
            <div className="bg-gray-700/50 rounded-lg p-4 border border-purple-500/20">
              <p className="text-gray-300 text-sm text-center">
                Access restricted to invited users only.
              </p>
            </div>

            {/* Remember Me Checkbox */}
            <div className="flex items-center justify-between">
              <label className="flex items-center space-x-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                  className="w-4 h-4 rounded border-gray-600 bg-gray-700 text-purple-600 focus:ring-purple-500 focus:ring-offset-0"
                />
                <span className="text-gray-300 text-sm">Remember me</span>
              </label>
            </div>

            {/* Login Button */}
            <button
              onClick={handleLogin}
              disabled={isLoading}
              className="w-full relative group overflow-hidden rounded-lg bg-gradient-to-r from-purple-600 to-pink-600 p-[2px] hover:from-purple-500 hover:to-pink-500 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed">
              <div className="relative bg-gray-900 rounded-lg py-3 px-6 group-hover:bg-opacity-90 transition-all duration-300">
                {isLoading ? (
                  <div className="flex items-center justify-center space-x-2">
                    <div className="w-5 h-5 border-2 border-purple-500 border-t-transparent rounded-full animate-spin" />
                    <span className="text-white font-medium">
                      Connecting...
                    </span>
                  </div>
                ) : (
                  <span className="text-white font-medium text-lg">
                    Sign In to Continue
                  </span>
                )}
              </div>
            </button>

            {/* Request Access Section */}
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-600"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 bg-gray-800 text-gray-400">
                  Don't have access?
                </span>
              </div>
            </div>

            {/* Request Access Button */}
            <button
              onClick={() => setIsModalOpen(true)}
              disabled={isLoading}
              className="w-full py-3 px-6 bg-gray-700/50 hover:bg-gray-700 text-gray-300 hover:text-white rounded-lg font-medium border border-gray-600 hover:border-purple-500/50 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed">
              Request an Invitation
            </button>

            {/* Security Notice */}
            <div className="text-center space-y-2 pt-2">
              <p className="text-gray-400 text-xs">
                Protected by enterprise-grade security
              </p>
              <div className="flex justify-center items-center space-x-4 text-gray-500">
                <svg
                  className="w-4 h-4"
                  fill="currentColor"
                  viewBox="0 0 20 20">
                  <path
                    fillRule="evenodd"
                    d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z"
                    clipRule="evenodd"
                  />
                </svg>
                <span className="text-xs">Encrypted Connection</span>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="mt-6 text-center">
          <p className="text-gray-500 text-sm">
            © {currentYear} Hermes Terminal. All rights reserved.
          </p>
        </div>
      </div>

      {/* Request Access Modal */}
      <RequestAccessModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
      />
    </div>
  );
};

export default LoginPage;
